let module = angular.module('app.services.multi-part-upload', []);

const MAX_SIMULTANEOUS_UPLOAD_PARTS = 4;
const MAX_BACKOFF_MS = 10000;
const EXP_BACKOFF_FACTOR = 1.75;

module.service('MultiPartUpload', function ($q, $http, $timeout, Upload) {
  class MultiPartUpload {
    constructor(baseUrl, file, parts) {
      this._baseUrl = baseUrl;
      this._file = file;
      this._parts = parts;
      this._destroyed = false;

      this._totalUploadSize = _.reduce(this._parts, (m, p) => m + p.size, 0);
      this._advanceDelay = 0;

      this._deferred = $q.defer();
      this._promise = this._deferred.promise;

      // Glue on a state for each part
      _.each(this._parts, (p) => {
        if (p.complete) {
          p.state = 'complete';
        } else {
          p.state = 'not-started';
        }
      });

      this._advance();
    }

    get $promise() {
      return this._promise;
    }

    destroy() {
      this._destroyed = true;
      if (this._timeout) {
        $timeout.cancel(this._timeout);
      }
    }

    // This function advances the state of the multi-part upload
    _advance() {
      if (this._destroyed) return;

      // Each part has an state (internal, maintained by this class, NOT provided from the API), one of:
      //  - not-started
      //  - uploading (PUT of the part content in flight)
      //  - uploaded (will have set complete: true and etag: <something>)
      //  - updating (PUT of the part status in flight)
      //  - complete

      // If there are >= MAX_SIMULTANEOUS_UPLOAD_PARTS parts still in flight, nothing to do.
      const numInFlight = _.filter(this._parts, (p) =>
        _.contains(['uploading', 'updating'], p.state)
      ).length;
      if (numInFlight >= MAX_SIMULTANEOUS_UPLOAD_PARTS) return;

      // First, let's look for a part to update
      const partToUpdate = _.find(this._parts, (p) => p.state === 'uploaded');
      if (partToUpdate) {
        this._startPartUpdate(partToUpdate);
        // Call ourselves recursively, as we might be able to start more.
        return this._advance();
      }

      // Next, let's go looking for a part to upload
      const partToUpload = _.find(this._parts, (p) => p.state === 'not-started');
      if (partToUpload) {
        partToUpload.$promise = this._startPartUpload(partToUpload);
        // Call ourselves recursively, as we might be able to start more.
        return this._advance();
      }

      // If there are still some in flight, wait until they're done
      if (numInFlight > 0) return;

      // All done, I guess... this promise _should_ resolve immediately since all the child
      // promises have already resolved.
      $q.all(_.map(this._parts, (p) => p.$promise)).then(
        (r) => this._deferred.resolve(r),
        (e) => this._deferred.reject(e)
      );
    }

    // This function calls _advance asynchronously with the appropriate backoff
    _advanceAsync(lastCallSuccessful) {
      this._timeout = $timeout(() => this._advance(), this._nextAdvanceDelay(lastCallSuccessful));
    }

    // Get the next delay for calling _advance
    _nextAdvanceDelay(lastCallSuccessful) {
      if (lastCallSuccessful) {
        this._advanceDelay = 0;
      } else {
        this._advanceDelay = Math.min(
          Math.max(1000, this._advanceDelay) * EXP_BACKOFF_FACTOR,
          MAX_BACKOFF_MS
        );
      }
      return this._advanceDelay;
    }

    // A function to notify the promise of progress; it is called with an individual
    // progress event, and converts it into a new event that summarizes total progress.
    _notifyProgress(e) {
      const newEvent = {
        loaded: this._totalLoaded(),
        total: this._totalUploadSize,
        type: e.type,
        config: e.config,
        lengthComputable: true,
        target: e.target,
      };
      if (this._deferred.notify) {
        this._deferred.notify(newEvent);
      }
      if (this._promise.progressFunc) {
        $timeout(() => this._promise.progressFunc(newEvent));
      }
    }

    // Called after successfully updating a part status with the API
    _partUpdateComplete(resp, part) {
      part.state = 'complete';
      this._advanceAsync(true);
    }

    // Called if updating a part status with the API fails
    _partUpdateError(err, part) {
      console.error('Error updating part:', part, err);
      part.state = 'uploaded';
      this._advanceAsync(false);
    }

    // Called when a part upload has completed successfully
    _partUploadComplete(resp, part) {
      part.complete = true;
      part.etag = resp.headers('ETag');
      part.state = 'uploaded';
      this._advanceAsync(true);
    }

    // Called when a part upload failed
    _partUploadError(err, part) {
      console.error('Error uploading part:', part, err);
      part.state = 'not-started';

      // The upload service refreshes the presigned PUT URLs for any incomplete parts when we GET
      // the upload again. Refresh it here and update the presigned URL for any incomplete parts
      // in case we've hit their signature expiration.
      $http.get(this._baseUrl).then(
        (resp) => {
          _.each(
            _.where(this._parts, (p) => _.contains(['not-started', 'uploading'], p.state)),
            (p) => {
              const refreshedPart = _.find(
                resp.data.parts,
                (p2) => p2.part_number === p.part_number
              );
              if (refreshedPart) {
                p.presigned_url = refreshedPart.presigned_url;
              }
            }
          );
          this._advanceAsync(false);
        },
        (err) => {
          console.error('Error refreshing upload:', err);
          this._advanceAsync(false);
        }
      );
    }

    // Called for progress/notify on a part upload
    _partUploadProgress(evt, part) {
      part.loaded = evt.loaded;
      this._notifyProgress(evt);
    }

    // Start the update of a part status with the API and return its promise
    _startPartUpdate(part) {
      part.state = 'updating';
      return $http
        .put(`${this._baseUrl}/parts/${part.part_number}`, {
          complete: part.complete,
          etag: part.etag,
        })
        .then(
          (r) => this._partUpdateComplete(r, part),
          (e) => this._partUpdateError(e, part)
        );
    }

    // Start the upload of a part and return its promise
    _startPartUpload(part) {
      part.state = 'uploading';
      return Upload.http({
        url: part.presigned_url,
        headers: {
          'Content-Type': this._file.type,
        },
        method: 'PUT',
        data: this._file.slice(part.offset, part.offset + part.size),
      }).then(
        (r) => this._partUploadComplete(r, part),
        (e) => this._partUploadError(e, part),
        (n) => this._partUploadProgress(n, part)
      );
    }

    // A function to calculate total loaded amount across all promises
    _totalLoaded() {
      return _.reduce(this._parts, (m, p) => m + (p.loaded || 0), 0);
    }
  }

  this.destroy = function () {
    if (this._mpu) {
      this._mpu.destroy();
    }
  };

  this.start = function (baseUrl, file, parts) {
    this._mpu = new MultiPartUpload(baseUrl, file, parts);
    return this._mpu.$promise;
  };
});

export default module;



// WEBPACK FOOTER //
// ./app/services/multi-part-upload.js